import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

import * as knowledgeAPI from '@/api/knowledge/api';
import type { ListKnowledgeCardsParams } from '@/api/knowledge/api';
import { mapKnowledgeCardListDTO } from '@/models/knowledge-card.mapper';
import { KnowledgeCard } from '@/models/knowledge-card.model';
import { createLogger } from '@/utils/logging';

const logger = createLogger('KnowledgeCardStore', 'hook');

/* ------------------------------------------------------------------ */
/*  State                                                              */
/* ------------------------------------------------------------------ */

export interface KnowledgeCardState {
  /** Current page of knowledge cards. */
  cards: KnowledgeCard[];
  /** Total matching cards across all pages (for pagination). */
  total: number;
  /** Current page number (1-indexed). */
  page: number;
  /** Items per page. */
  pageSize: number;
  loading: boolean;
  error: string | null;
}

const initialState: KnowledgeCardState = {
  cards: [],
  total: 0,
  page: 1,
  pageSize: 20,
  loading: false,
  error: null,
};

/* ------------------------------------------------------------------ */
/*  Actions                                                            */
/* ------------------------------------------------------------------ */

export enum KnowledgeCardActionType {
  FETCH_START = 'FETCH_START',
  FETCH_SUCCESS = 'FETCH_SUCCESS',
  FETCH_ERROR = 'FETCH_ERROR',
  CLEAR_ERROR = 'CLEAR_ERROR',
}

interface FetchSuccessPayload {
  cards: KnowledgeCard[];
  total: number;
  page: number;
  pageSize: number;
}

export type KnowledgeCardAction =
  | { type: KnowledgeCardActionType.FETCH_START }
  | { type: KnowledgeCardActionType.FETCH_SUCCESS; payload: FetchSuccessPayload }
  | { type: KnowledgeCardActionType.FETCH_ERROR; payload: string }
  | { type: KnowledgeCardActionType.CLEAR_ERROR };

/* ------------------------------------------------------------------ */
/*  Reducer                                                            */
/* ------------------------------------------------------------------ */

const knowledgeCardReducer = (
  state: KnowledgeCardState,
  action: KnowledgeCardAction,
): KnowledgeCardState => {
  switch (action.type) {
    case KnowledgeCardActionType.FETCH_START:
      return { ...state, loading: true, error: null };

    case KnowledgeCardActionType.FETCH_SUCCESS: {
      const { cards, total, page, pageSize } = action.payload;
      return {
        ...state,
        cards,
        total,
        page,
        pageSize,
        loading: false,
        error: null,
      };
    }

    case KnowledgeCardActionType.FETCH_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload ?? 'An error occurred',
      };

    case KnowledgeCardActionType.CLEAR_ERROR:
      return { ...state, error: null };

    default:
      return state;
  }
};

/* ------------------------------------------------------------------ */
/*  Context type                                                       */
/* ------------------------------------------------------------------ */

interface KnowledgeCardContextType extends KnowledgeCardState {
  /** Fetch a page of knowledge cards for the given client. */
  fetchCards: (
    accessToken: string,
    clientId: string,
    params?: ListKnowledgeCardsParams,
  ) => Promise<void>;
  clearError: () => void;
}

const KnowledgeCardContext = createContext<KnowledgeCardContextType | undefined>(undefined);

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

/** Props for the KnowledgeCardProvider component. */
export interface KnowledgeCardProviderProps {
  children: ReactNode;
}

/**
 * Provides paginated knowledge card state and a `fetchCards` action to the React tree.
 * Consumers call `fetchCards` to load or reload a page; state is replaced on each call.
 */
export const KnowledgeCardProvider: React.FC<KnowledgeCardProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(knowledgeCardReducer, initialState);

  const fetchCards = useCallback(
    async (accessToken: string, clientId: string, params?: ListKnowledgeCardsParams) => {
      dispatch({ type: KnowledgeCardActionType.FETCH_START });
      try {
        const response = await knowledgeAPI.listKnowledgeCards(accessToken, clientId, params);
        const cards = mapKnowledgeCardListDTO(response.items);
        dispatch({
          type: KnowledgeCardActionType.FETCH_SUCCESS,
          payload: {
            cards,
            total: response.total,
            page: params?.page ?? 1,
            pageSize: params?.page_size ?? 20,
          } satisfies FetchSuccessPayload,
        });
        logger.info('Knowledge cards fetched', { count: cards.length, total: response.total });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch cards';
        dispatch({ type: KnowledgeCardActionType.FETCH_ERROR, payload: errorMessage });
        logger.error('Failed to fetch knowledge cards', error);
      }
    },
    [],
  );

  const clearError = useCallback(() => {
    dispatch({ type: KnowledgeCardActionType.CLEAR_ERROR });
  }, []);

  const value = useMemo(
    (): KnowledgeCardContextType => ({
      ...state,
      fetchCards,
      clearError,
    }),
    [state, fetchCards, clearError],
  );

  return <KnowledgeCardContext.Provider value={value}>{children}</KnowledgeCardContext.Provider>;
};

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * Accesses the knowledge card store from the nearest KnowledgeCardProvider.
 * Throws if called outside a KnowledgeCardProvider.
 *
 * @returns Paginated card list, loading/error state, and fetch/clearError actions.
 */
export const useKnowledgeCardContext = (): KnowledgeCardContextType => {
  const context = useContext(KnowledgeCardContext);
  if (!context) {
    throw new Error('useKnowledgeCardContext must be used within KnowledgeCardProvider');
  }
  return context;
};
